import React, { useState, useEffect } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import UserTeamModal from './UserTeamModal.tsx';
import { updateUser } from '../../../services/usersService';
import { useToastContext as useToast } from '../../../context/ToastContext';
import { getTeamImage } from '../../../models/TeamConstants';
import { usePostHog } from '@posthog/react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../../hooks/useUsers';
import { useAchievements } from '../../../hooks/useAchievements';
import { groupAchievementsForPeriod } from '../../achievements/achievementHelpers';
import AchievementBadgeRow from '../../achievements/components/AchievementBadgeRow';

const ACHIEVEMENTS_LABEL_COLOR = '#ce93d8';

export default function ProfilePage({
    user,
    onUpdateUser,
}) {
    const queryClient = useQueryClient();
    const theme = useTheme();
    const { addToast } = useToast();
    const posthog = usePostHog();

    // Fetch fresh stats from cache — avoids timing issues with unmount saves
    const { data: freshUser } = useUser(user?.userId ?? '');
    // Use freshUser for stats if available, fall back to prop
    const displayUser = freshUser ?? user;

    const { data: achievements } = useAchievements(user?.userId ?? '');
    const badgeGroups = groupAchievementsForPeriod(achievements ?? [], 'alltime');

    const [open, setOpen] = useState(false);
    const [team, setTeam] = useState(
        user?.team && user.team.trim() !== ''
            ? user.team
            : null
    );

    const [isEditing, setIsEditing] = useState(false);
    const [editedUsername, setEditedUsername] = useState(
        user?.username || ''
    );
    const [editedEmail, setEditedEmail] = useState(
        user?.email || ''
    );
    const [loading, setLoading] = useState(false);

    // Sync local edited fields whenever `user` changes
    useEffect(() => {
        if (user) {
            setEditedUsername(user.username || '');
            setEditedEmail(user.email || '');
            setTeam(
                user.team && user.team.trim() !== ''
                    ? user.team
                    : null
            );
        }
    }, [
        user?.userId,
        user?.totalQuestionsAnswered,
        user?.totalCorrectAnswers,
        user?.username,
        user?.email,
        user?.team,
    ]);

    // Scroll to hash anchor after render — handles /profile#achievements navigation.
    // A small delay accounts for the card rendering before the browser can scroll.
    useEffect(() => {
        if (!window.location.hash) return;
        const id = window.location.hash.slice(1);
        const timer = setTimeout(() => {
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    // Unified update handler for username/email/team
    const handleUserUpdate = async (updates) => {
        if (!user) return;

        setLoading(true);
        try {
            // Only send userId and the fields being updated
            const response = await updateUser({
                userId: user.userId,
                ...updates,  // Only the changed fields
            });

            // Backend returns the full, fresh user object
            localStorage.setItem('triviaUser', JSON.stringify(response));
            queryClient.invalidateQueries({ queryKey: ['users'] });
            
            if (onUpdateUser) {
                onUpdateUser(response);
            }

            addToast('Profile updated successfully!', { type: 'success' });
        } catch (err) {
            console.error('Failed to update user:', err);
            addToast('Could not update profile. Please try again.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleTeamChange = async (newTeam) => {
        const normalizedTeam =
            newTeam && newTeam.trim() !== ''
                ? newTeam
                : null;

        setTeam(normalizedTeam);
        posthog?.capture('team_changed', { team: normalizedTeam });
        await handleUserUpdate({ team: normalizedTeam });
    };

    const handleToggleEdit = async () => {
        if (isEditing) {
            posthog?.capture('profile_updated', { username: editedUsername });
            await handleUserUpdate({
                username: editedUsername,
                email: editedEmail,
            });
        }
        setIsEditing(!isEditing);
    };

    if (!user) {
        return (
            <Box
                display='flex'
                justifyContent='center'
                mt={10}
            >
                <Typography
                    variant='h6'
                    color='text.secondary'
                >
                    Loading user info...
                </Typography>
            </Box>
        );
    }

    const createdAt = new Date(
        user.createdAt
    ).toLocaleDateString();
    const updatedAt = new Date(
        user.updatedAt
    ).toLocaleString([], {
        dateStyle: 'short',
        timeStyle: 'short',
    });

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor:
                    theme.palette.background.default,
                color: theme.palette.text.primary,
                p: 2,
            }}
        >
            <Card
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    backgroundColor:
                        theme.palette.background.paper,
                    borderRadius: 3,
                    boxShadow: theme.shadows[6],
                }}
            >
                <CardContent>
                    <Box
                        display='flex'
                        flexDirection='column'
                        alignItems='center'
                        mb={2}
                    >
                        <Avatar
                            src={user.avatarUrl}
                            alt={user.username}
                            sx={{
                                width: 100,
                                height: 100,
                                mb: 2,
                                border: `2px solid ${theme.palette.primary.main}`,
                            }}
                        />

                        {isEditing ? (
                            <>
                                <TextField
                                    label='Username'
                                    variant='outlined'
                                    size='small'
                                    fullWidth
                                    value={editedUsername}
                                    onChange={(e) =>
                                        setEditedUsername(
                                            e.target.value
                                        )
                                    }
                                    sx={{ mb: 1 }}
                                />
                                <TextField
                                    label='Email'
                                    variant='outlined'
                                    size='small'
                                    fullWidth
                                    value={editedEmail}
                                    onChange={(e) =>
                                        setEditedEmail(
                                            e.target.value
                                        )
                                    }
                                    sx={{ mb: 1 }}
                                />
                            </>
                        ) : (
                            <>
                                <Typography
                                    variant='h5'
                                    fontWeight={600}
                                >
                                    {user.username}
                                </Typography>
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    {user.email}
                                </Typography>
                            </>
                        )}

                        <Typography
                            variant='caption'
                            sx={{ opacity: 0.8, mt: 0.5 }}
                        >
                            Role: {user.userRole}
                        </Typography>

                        <Button
                            variant='contained'
                            onClick={handleToggleEdit}
                            disabled={loading}
                            sx={{
                                mt: 2,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                        >
                            {isEditing
                                ? 'Save Changes'
                                : 'Edit Profile'}
                        </Button>
                    </Box>

                    <Divider
                        sx={{
                            my: 2,
                            backgroundColor:
                                'rgba(255,255,255,0.12)',
                        }}
                    />

                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant='body1'
                            gutterBottom
                            textAlign='center'
                        >
                            <strong>Team</strong>
                        </Typography>

                        <Box
                            display='flex'
                            flexDirection='column'
                            alignItems='center'
                            gap={1}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor:
                                    theme.palette.action
                                        .hover,
                                mb: 2,
                            }}
                        >
                            <Typography
                                variant='h1'
                                sx={{
                                    fontSize: '4rem',
                                    lineHeight: 1,
                                    mb: 1,
                                }}
                            >
                                {getTeamImage(team)}
                            </Typography>
                            <Typography
                                variant='h6'
                                fontWeight={600}
                            >
                                {team || 'No team chosen'}
                            </Typography>
                        </Box>

                        <Box
                            display='flex'
                            justifyContent='center'
                            mb={2}
                        >
                            <Button
                                variant='contained'
                                color='primary'
                                onClick={() =>
                                    setOpen(true)
                                }
                                disabled={loading}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    px: 3,
                                }}
                            >
                                {team
                                    ? 'Change Team'
                                    : 'Choose Your Team'}
                            </Button>
                        </Box>

                        {/* Achievements */}
                        <Divider id="achievements" sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.12)' }} />

                        {/* Label — matches leaderboard card style */}
                        <Typography sx={{
                            fontSize: 14,
                            color: ACHIEVEMENTS_LABEL_COLOR,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            mb: 1,
                            textAlign: 'center',
                        }}>
                            Achievements
                        </Typography>

                        {badgeGroups.length === 0 ? (
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                textAlign='center'
                                sx={{ mb: 1 }}
                            >
                                No achievements yet — top the leaderboard to earn badges!
                            </Typography>
                        ) : (
                            <AchievementBadgeRow badgeGroups={badgeGroups} justify="center" />
                        )}

                        {/* Stats */}
                        <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.12)' }} />

                        <Typography variant='body1'>
                            <strong>Total Questions Answered:</strong>{' '}
                            {displayUser.totalQuestionsAnswered}  {/* was user. */}
                        </Typography>
                        <Typography variant='body1'>
                            <strong>Total Correct Answers:</strong>{' '}
                            {displayUser.totalCorrectAnswers}  {/* was user. */}
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                display='block'
                            >
                                Member since: {createdAt}
                            </Typography>
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                display='block'
                            >
                                Last updated: {updatedAt}
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            <UserTeamModal
                open={open}
                onClose={() => setOpen(false)}
                selectedTeam={team}
                onTeamSelect={handleTeamChange}
            />
        </Box>
    );
}